import { HttpContext } from "@angular/common/http";
import { inject } from "@angular/core";
import { ActivatedRouteSnapshot, ResolveFn } from "@angular/router";
import { catchError, forkJoin, map, Observable, of } from "rxjs";
import { pathParamsHelper } from "../helpers/path-params.helper";
import { TechDataComparison } from "../models/tech-data-comparison";
import { DefaultTechData } from "../pia-api/generated";
import { PiaCompetitorsService } from "../pia-api/pia-competitors.service";
import { PiaProductDetailService } from "../pia-api/pia-product-detail.service";
import { IS_IN_RESOLVER_CONTEXT } from "../tokens/resolver-context.token";
import { withLoading } from "../utils/with-loading";

export const compareTechDataResolver: ResolveFn<
  Observable<TechDataComparison[] | null>
> = withLoading((route: ActivatedRouteSnapshot) => {
  const { competitorID, productDetailID, countryID, languageID, year } =
    pathParamsHelper(route);

  const productDetailsService = inject(PiaProductDetailService);
  const competitorsService = inject(PiaCompetitorsService);
  const context = new HttpContext().set(IS_IN_RESOLVER_CONTEXT, true);

  return forkJoin({
    productTechData: productDetailsService.getProductTechDataByProductId(
      {
        countryID,
        languageID,
        year,
        productID: productDetailID,
      },
      context,
    ),
    competitorTechData:
      competitorsService.getCompetitorTechDataByContextAndCompetitorProductId({
        competitorProductId: competitorID,
        year,
        countryID,
        languageID,
      }),
  }).pipe(
    map(({ productTechData, competitorTechData }) => {
      const compMap = new Map(
        competitorTechData?.d?.results?.map((spec) => [
          spec.CategoryId,
          spec.Value,
        ]) ?? [],
      );

      const result = (productTechData ?? [])
        .map(
          (spec) =>
            ({
              ...spec,
              competitorSpecValue: compMap.get(spec.CategoryId) ?? null,
            }) as DefaultTechData & { competitorSpecValue: string | null },
        )
        .sort((a, b) => (a.Category?.Sort ?? 0) - (b.Category?.Sort ?? 0));
      return result;
    }),
    catchError((error) => {
      console.error(error);
      return of(null);
    }),
  );
});
